import React, { createContext, useReducer, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * AuthContext - Provides authentication state and permissions to the entire app.
 * Calls /is-authenticated once on mount and stores user context including permissions.
 */
export const AuthContext = createContext();

/**
 * Action types for the auth reducer
 */
const AUTH_ACTIONS = {
    AUTH_START: 'AUTH_START',
    AUTH_SUCCESS: 'AUTH_SUCCESS',
    AUTH_FAILURE: 'AUTH_FAILURE',
    LOGOUT: 'LOGOUT',
    REFRESH_START: 'REFRESH_START',
};

/**
 * Initial state for auth context
 */
const initialState = {
    userId: null,
    email: null,
    businessId: null,
    businessPermissions: [], // string[] - permission enum names
    projectAccess: {}, // { [projectId]: { role: string, permissions: string[] } }
    isLoading: true,
    isAuthenticated: false,
    error: null,
};

/**
 * Auth reducer to handle state updates
 */
const authReducer = (state, action) => {
    switch (action.type) {
        case AUTH_ACTIONS.AUTH_START:
            return {
                ...state,
                isLoading: true,
                error: null,
            };

        case AUTH_ACTIONS.AUTH_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isAuthenticated: true,
                userId: action.payload.userId,
                email: action.payload.email,
                businessId: action.payload.businessId,
                businessPermissions: action.payload.businessPermissions || [],
                projectAccess: action.payload.projectAccess || {},
                error: null,
            };

        case AUTH_ACTIONS.AUTH_FAILURE:
            return {
                ...state,
                isLoading: false,
                isAuthenticated: false,
                userId: null,
                email: null,
                businessId: null,
                businessPermissions: [],
                projectAccess: {},
                error: action.payload,
            };

        case AUTH_ACTIONS.LOGOUT:
            return {
                ...initialState,
                isLoading: false,
            };

        case AUTH_ACTIONS.REFRESH_START:
            return {
                ...state,
                isLoading: true,
            };

        default:
            return state;
    }
};

/**
 * AuthProvider component that wraps the app and provides auth context
 */
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    /**
     * Check authentication status by calling /is-authenticated endpoint.
     * This endpoint returns user context with permissions (PR #1 enhancement).
     */
    const checkAuth = useCallback(async () => {
        dispatch({ type: AUTH_ACTIONS.AUTH_START });

        try {
            const response = await fetch(`${API_URL}/is-authenticated`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                const data = await response.json();
                dispatch({
                    type: AUTH_ACTIONS.AUTH_SUCCESS,
                    payload: {
                        userId: data.userId,
                        email: data.email,
                        businessId: data.businessId,
                        businessPermissions: data.businessPermissions || [],
                        projectAccess: data.projectAccess || {},
                    },
                });
            } else {
                dispatch({
                    type: AUTH_ACTIONS.AUTH_FAILURE,
                    payload: 'Not authenticated',
                });
            }
        } catch (error) {
            dispatch({
                type: AUTH_ACTIONS.AUTH_FAILURE,
                payload: error.message || 'Authentication check failed',
            });
        }
    }, []);

    /**
     * Refresh permissions - call this after admin changes a user's role or
     * if someone is added to a project or business.
     * This will clear the auth state and re-fetch the permissions.
     * TODO: call this when creating a new project and adding that current user to it.
     */
    const refreshPermissions = useCallback(async () => {
        dispatch({ type: AUTH_ACTIONS.REFRESH_START });
        await checkAuth();
    }, [checkAuth]);

    /**
     * Logout - clear auth state
     */
    const logout = useCallback(() => {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }, []);

    // Check auth on mount
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const value = {
        // Auth state
        userId: state.userId,
        email: state.email,
        businessId: state.businessId,
        businessPermissions: state.businessPermissions,
        projectAccess: state.projectAccess,
        isLoading: state.isLoading,
        isAuthenticated: state.isAuthenticated,
        error: state.error,

        // Actions
        refreshPermissions,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
